import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtUSD, fmtNum, relTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Copy as CopyIcon, X, ArrowUpRight, ArrowDownRight, Sparkles, Users, ShieldAlert } from "lucide-react";
import { Input } from "@/components/ui/input";
import { TierBadge } from "@/pages/Leaderboard";

export default function CopyPage() {
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [skips, setSkips] = useState([]);
  const [saving, setSaving] = useState(false);
  const [limits, setLimits] = useState({ max_per_trade_usd: "", daily_loss_cap_usd: "" });

  const load = async () => {
    try {
      const [{ data: s }, { data: h }, { data: sk }] = await Promise.all([
        api.get("/copy/settings"),
        api.get("/copy/history", { params: { limit: 30 } }),
        api.get("/copy/skips", { params: { limit: 10 } }),
      ]);
      setSettings(s);
      setHistory(h);
      setSkips(sk || []);
      setLimits({
        max_per_trade_usd: s.max_per_trade_usd ? String(s.max_per_trade_usd) : "",
        daily_loss_cap_usd: s.daily_loss_cap_usd ? String(s.daily_loss_cap_usd) : "",
      });
    } catch {
      // ignore
    }
  };
  useEffect(() => { load(); }, []);

  const patch = async (body) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/copy/settings", body);
      setSettings(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const removeSource = async (id) => {
    try {
      await api.delete(`/copy/follow/${id}`);
      toast.success("Removed from copy list");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove");
    }
    load();
  };

  if (!settings) return <p className="text-sm text-gray-500">Loading copy settings…</p>;

  const copiedTotal = history.reduce((sum, t) => sum + Number(t.total || 0), 0);

  return (
    <div className="space-y-8" data-testid="copy-root">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Copy trading</p>
        <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-2 flex items-center gap-3">
          Auto-mirror the best <CopyIcon className="text-gain" size={28} />
        </h1>
        <p className="text-gray-500 mt-2 max-w-2xl">
          Every trade a trader you follow makes gets proportionally mirrored to your paper account (optionally routed
          to Alpaca). Enable up to {settings.max_sources} sources with a max {settings.max_allocation_pct}% allocation.
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Settings */}
        <Card className="lg:col-span-5 border border-gray-200 rounded-2xl p-6 h-fit" data-testid="copy-settings-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-display font-semibold text-lg">Copy engine</p>
              <p className="text-xs text-gray-500 mt-1">
                {settings.enabled ? "Active — new trades will mirror" : "Paused — no mirrors will fire"}
              </p>
            </div>
            <Switch
              data-testid="copy-enabled-switch"
              checked={settings.enabled}
              onCheckedChange={(v) => patch({ enabled: !!v })}
              disabled={saving}
            />
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500">
                Allocation per trade
              </label>
              <span className="text-sm font-mono font-medium" data-testid="allocation-value">
                {Number(settings.allocation_pct).toFixed(1)}%
              </span>
            </div>
            <Slider
              data-testid="allocation-slider"
              min={0.5}
              max={settings.max_allocation_pct}
              step={0.5}
              value={[Number(settings.allocation_pct)]}
              onValueCommit={(v) => patch({ allocation_pct: v[0] })}
              disabled={saving}
            />
            <p className="text-[11px] text-gray-400 mt-2">
              Each mirrored order is sized as {settings.allocation_pct}% of the source trader&apos;s notional, and never
              consumes more than {settings.allocation_pct}% of your cash on a single fill.
            </p>
          </div>

          <div className="mt-6">
            <p className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500 mb-2">Per-copy risk limits</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-[11px] text-gray-500">Sizing mode</label>
                <div className="flex gap-1 mt-1 rounded-full bg-gray-100 p-0.5">
                  {[{ k: "pct", l: "% of trade" }, { k: "fixed", l: "Fixed $" }].map((o) => (
                    <button key={o.k} data-testid={`copy-mode-${o.k}`}
                      onClick={() => patch({ allocation_mode: o.k })}
                      className={`flex-1 rounded-full text-[11px] font-medium py-1.5 ${(settings.allocation_mode || "pct") === o.k ? "bg-white shadow-sm" : "text-gray-500"}`}>
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[11px] text-gray-500">Fixed $ per mirror</label>
                <Input data-testid="copy-fixed-usd" type="number" placeholder="e.g. 100"
                  defaultValue={settings.fixed_alloc_usd || ""}
                  onBlur={(e) => patch({ fixed_alloc_usd: parseFloat(e.target.value) || 0 })}
                  className="mt-1 h-9 text-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-gray-500">Max per mirrored trade $</label>
                <Input data-testid="copy-max-per-trade" type="number" placeholder="off"
                  value={limits.max_per_trade_usd}
                  onChange={(e) => setLimits((l) => ({ ...l, max_per_trade_usd: e.target.value }))}
                  onBlur={() => patch({ max_per_trade_usd: parseFloat(limits.max_per_trade_usd) || 0 })}
                  className="mt-1 h-9 text-sm" />
              </div>
              <div>
                <label className="text-[11px] text-gray-500">Daily copy-loss cap $</label>
                <Input data-testid="copy-daily-loss-cap" type="number" placeholder="off"
                  value={limits.daily_loss_cap_usd}
                  onChange={(e) => setLimits((l) => ({ ...l, daily_loss_cap_usd: e.target.value }))}
                  onBlur={() => patch({ daily_loss_cap_usd: parseFloat(limits.daily_loss_cap_usd) || 0 })}
                  className="mt-1 h-9 text-sm" />
              </div>
              <div>
                <label className="text-[11px] text-gray-500">Max concurrent positions</label>
                <Input data-testid="copy-max-positions" type="number" placeholder="off"
                  defaultValue={settings.max_concurrent_positions || ""}
                  onBlur={(e) => patch({ max_concurrent_positions: parseInt(e.target.value) || 0 })}
                  className="mt-1 h-9 text-sm" />
              </div>
              <div className="flex items-end">
                <Button data-testid="copy-kill-btn" variant="outline" disabled={saving}
                  onClick={async () => {
                    try {
                      const { data } = await api.post("/copy/kill");
                      toast.success(`Copying disabled — ${data.liquidated.length} position(s) liquidated`);
                      load();
                    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
                  }}
                  className="w-full h-9 rounded-full border-rose-300 text-rose-600 hover:bg-rose-50 text-xs">
                  Emergency kill
                </Button>
              </div>
            </div>
            <p className="text-[11px] text-gray-400 mt-2">
              When the cap is hit, new mirrored buys pause for the day (exits still mirror). Every mirror also passes the risk engine.
            </p>
          </div>

          <div className="mt-6">
            <p className="text-xs uppercase tracking-[0.1em] font-bold text-gray-500 mb-2">Destination</p>
            <div className="grid grid-cols-2 gap-1 rounded-full bg-gray-100 p-1">
              {[
                { k: "paper", label: "Paper (TradePilot)" },
                { k: "alpaca", label: "Alpaca (if connected)" },
              ].map((o) => (
                <button
                  key={o.k}
                  data-testid={`copy-dest-${o.k}`}
                  onClick={() => patch({ destination: o.k })}
                  className={`rounded-full text-xs font-medium py-2 ${
                    settings.destination === o.k ? "bg-white text-black shadow-sm" : "text-gray-600"
                  }`}
                  disabled={saving}
                >
                  {o.label}
                </button>
              ))}
            </div>
            {settings.destination === "alpaca" && (
              <p className="text-[11px] text-gray-400 mt-2">
                Alpaca mirroring is best-effort — silently no-ops if your Alpaca broker isn&apos;t connected on the{" "}
                <Link to="/broker" className="underline">Broker page</Link>.
              </p>
            )}
          </div>
        </Card>

        {/* Source traders */}
        <Card className="lg:col-span-7 border border-gray-200 rounded-2xl p-6" data-testid="copy-sources-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users size={16} />
              <p className="font-display font-semibold text-lg">
                Traders you copy{" "}
                <span className="text-gray-400 text-sm font-normal">
                  ({settings.sources?.length || 0}/{settings.max_sources})
                </span>
              </p>
            </div>
            <Link
              to="/leaderboard"
              data-testid="copy-find-more"
              className="text-xs font-medium text-black hover:underline"
            >
              Find more →
            </Link>
          </div>

          {(!settings.sources || settings.sources.length === 0) ? (
            <div className="text-center py-10">
              <p className="text-sm text-gray-500">
                No source traders yet. Visit the{" "}
                <Link to="/leaderboard" className="text-black underline">Leaderboard</Link>{" "}
                or any trader&apos;s profile and hit &quot;Copy trader&quot; to add them here.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {settings.sources.map((s) => (
                <div
                  key={s.user_id}
                  className="flex items-center justify-between py-3"
                  data-testid={`source-${s.user_id}`}
                >
                  <Link to={`/profile/${s.user_id}`} className="flex items-center gap-3 flex-1 min-w-0">
                    <Avatar className="h-10 w-10 border border-gray-200">
                      <AvatarImage src={s.avatar_url} alt={s.name} />
                      <AvatarFallback>{s.name?.slice(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium flex items-center gap-1.5">
                        {s.name} <TierBadge tier={s.verification_tier} userId={s.user_id} />
                      </p>
                      <p className="text-xs text-gray-500">
                        {Number(settings.allocation_pct).toFixed(1)}% of each trade
                      </p>
                    </div>
                  </Link>
                  <button
                    onClick={() => removeSource(s.user_id)}
                    data-testid={`remove-source-${s.user_id}`}
                    className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-loss"
                    title="Stop copying"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Mirrored history */}
      {skips.length > 0 && (
        <Card className="border border-amber-200 bg-amber-50/40 rounded-2xl p-5" data-testid="copy-skips-card">
          <p className="text-sm font-semibold flex items-center gap-2 text-amber-800">
            <ShieldAlert size={14} /> Mirrors blocked by your limits
          </p>
          <div className="mt-2 space-y-1">
            {skips.map((k) => (
              <p key={k._id} className="text-xs text-amber-900/80" data-testid={`copy-skip-${k._id}`}>
                {k.side.toUpperCase()} {k.symbol} — {k.reason} <span className="text-amber-900/40">· {relTime(k.created_at)}</span>
              </p>
            ))}
          </div>
        </Card>
      )}
      <Card className="border border-gray-200 rounded-2xl p-6" data-testid="copy-history-card">
        <div className="flex items-center justify-between mb-4">
          <p className="font-display font-semibold text-lg">Mirrored trades</p>
          <p className="text-xs text-gray-500 font-mono">
            {history.length} mirror{history.length === 1 ? "" : "s"} · {fmtUSD(copiedTotal)} total volume
          </p>
        </div>
        {history.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-sm text-gray-500">
              No mirrored trades yet. Once your sources place a trade, you&apos;ll see the mirror here in seconds.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {history.map((t) => (
              <div key={t._id} className="flex items-center justify-between py-3" data-testid={`copy-history-${t._id}`}>
                <div className="flex items-center gap-3">
                  {t.side === "buy" ? (
                    <ArrowUpRight size={16} className="text-gain" />
                  ) : (
                    <ArrowDownRight size={16} className="text-loss" />
                  )}
                  <div>
                    <p className="text-sm font-medium">
                      <span className={t.side === "buy" ? "text-gain" : "text-loss"}>{t.side.toUpperCase()}</span>{" "}
                      {fmtNum(t.quantity)} {t.symbol}
                    </p>
                    <p className="text-xs text-gray-500">
                      @ {fmtUSD(t.price, { dp: t.price > 10 ? 2 : 4 })} · {relTime(t.created_at)}
                      {t.copied_from_user_id && (
                        <>
                          {" · from "}
                          <Link
                            to={`/profile/${t.copied_from_user_id}`}
                            className="text-black underline hover:no-underline"
                          >
                            trader
                          </Link>
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <p className="text-sm font-mono">{fmtUSD(t.total)}</p>
                  <Badge variant="secondary" className="text-[10px]">
                    <Sparkles size={10} className="mr-1" /> auto
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
